// @owners { team: patients }
import { isAfter, isToday, parseISO } from 'date-fns';
import { useMemo } from 'react';
import { formatDateWithDowWithoutYear } from '~shared/helpers/date';
import { getDoctorName } from '~shared/helpers/doctorName';
import { getErrorMessage } from '~shared/helpers/helper';
import { useSelectorShared } from '~shared/store';
import { type Prescription } from '~shared/types';

type Station = {
  id: 'request' | 'waiting' | 'resolved';
  title: string;
  description: string;
};

export type RenewalStatusTimelineConfig = {
  error?: string;
  loading: boolean;
  activeStation?: 'waiting' | 'request';
  stations: Record<string, Station>;
};

export const useRenewalStatusTimeline = (prescription: Prescription): RenewalStatusTimelineConfig => {
  const fetchRenewalRequestTimelineError = useSelectorShared(
    (state) => state.ui.errors.fetchRenewalRequestTimelineError,
  );
  const fetchRenewalRequestTimelineLoading = useSelectorShared(
    (state) => state.ui.loading.fetchRenewalRequestTimelineLoading,
  );

  const timeline = useSelectorShared((state) => {
    return state.prescriptions.renewalRequestTimelines?.[prescription.id];
  });

  // Compute the doctor name outside the memo so the memo depends on the resulting string (stable by
  // value) rather than the `prescription.doctor` object, which is a new reference on every Redux
  // selector result and would invalidate the memo spuriously.
  const doctorName = getDoctorName(prescription.doctor);

  const config: RenewalStatusTimelineConfig = useMemo(() => {
    // Build a fresh config object locally so we never mutate a value shared across renders.
    const nextConfig: RenewalStatusTimelineConfig = {
      error: getErrorMessage(fetchRenewalRequestTimelineError),
      loading: fetchRenewalRequestTimelineLoading,
      stations: {
        request: { id: 'request' } as Station,
        waiting: { id: 'waiting' } as Station,
        resolved: {
          id: 'resolved',
          title: 'Resolved',
          description: "We'll notify you when we get a response.",
        },
      },
    };

    if (!timeline?.length) {
      return nextConfig;
    }

    const renewalStartDateTime = parseISO(timeline[0].date);
    const renewalHasStarted = isToday(renewalStartDateTime) || isAfter(new Date(), renewalStartDateTime);
    const formattedDate = formatDateWithDowWithoutYear(renewalStartDateTime);

    if (timeline[0].failed) {
      nextConfig.stations.request.title = 'Request failed';
      nextConfig.stations.request.description =
        'We tried sending a request but it did not go through. We will call your doctor to request this for you.';
      nextConfig.stations.waiting.title = 'Wait for response';
      nextConfig.stations.waiting.description = 'If we do not get ahold of them, we will check back in a couple days.';
    } else if (renewalHasStarted) {
      nextConfig.activeStation = 'waiting';
      nextConfig.stations.request.title = 'Refills requested';
      nextConfig.stations.request.description = `On ${formattedDate}, we asked ${doctorName} for more refills of this medication.`;
      nextConfig.stations.waiting.title = 'Waiting on your provider';
      nextConfig.stations.waiting.description = 'We are waiting to hear back and will check again soon.';
    } else {
      nextConfig.activeStation = 'request';
      nextConfig.stations.request.title = 'Upcoming refill request';
      nextConfig.stations.request.description = `We're set to request refills from ${doctorName} on ${formattedDate}, closer to the next fill date.`;
      nextConfig.stations.waiting.title = 'Wait for response';
      nextConfig.stations.waiting.description = `We will follow up with ${doctorName} if we do not hear back.`;
    }

    return nextConfig;
  }, [fetchRenewalRequestTimelineError, fetchRenewalRequestTimelineLoading, timeline, doctorName]);

  return config;
};
